'use client';

import React from 'react';

import { useSearchParams } from 'next/navigation';

import type { Provider } from '@supabase/supabase-js';

import { SignInMethodsContainer } from '@kit/auth/sign-in';
import {
  NativeFaceIdButton,
  NativeSignInPage,
  clearBiometricCredentials,
  storeBiometricCredentials,
  useBiometricAuth,
  useNativeDetect,
} from '@kit/auth/native';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Heading } from '@kit/ui/heading';
import { Trans } from '@kit/ui/trans';

import { useTenantBrandingOptional } from '~/(tenant)/_components/tenant-branding-provider';

export function SignInContent({
  paths,
  providers,
  captchaSiteKey,
  signUpPath,
  passwordResetPath,
  logoUrl,
  accountName,
  primaryColor,
  secondaryColor,
  appBackgroundColor,
  appTextColor,
  displayChildrenSection,
}: {
  paths: {
    callback: string;
    returnPath: string;
    joinTeam: string;
  };
  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    phone: boolean;
    oAuth: Provider[];
  };
  captchaSiteKey?: string;
  signUpPath: string;
  passwordResetPath: string;
  logoUrl?: string | null;
  accountName?: string;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor: string;
  appTextColor: string;
  displayChildrenSection: boolean;
}) {
  const { isNative, isReady } = useNativeDetect();
  const tenantBranding = useTenantBrandingOptional();
  const tenantSlug = tenantBranding?.accountSlug;

  // Default to web on SSR + first client paint so web users get a fully-
  // rendered form with no blank flash. Swap to native once useNativeDetect
  // confirms the Capacitor runtime.
  if (isReady && isNative) {
    return (
      <NativeSignInWithBiometric
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
        signUpPath={signUpPath}
        passwordResetPath={passwordResetPath}
        captchaSiteKey={captchaSiteKey}
        providers={providers}
        tenantSlug={tenantSlug}
      />
    );
  }

  return (
    // ask-brand-neutral repoints ONLY --primary to ink, so the sign-in's primary
    // button + primary-colored links match the admin app (which wears the same
    // neutral) instead of the tenant's brand/orange — without touching surfaces,
    // type, or any other branding. Scoped to the WEB sign-in: the native consumer
    // login (NativeSignInPage, returned above) never reaches here and is
    // untouched. flex/gap-y-6 reproduces the AuthLayoutShell card's own child
    // spacing now that the heading + methods live inside one wrapper.
    <div className="ask-brand-neutral flex flex-col gap-y-6">
      <div className="flex flex-col items-center gap-1">
        <Heading level={4} className="tracking-tight">
          <Trans i18nKey="auth:signInHeading" />
        </Heading>

        <p className="text-muted-foreground text-sm">
          <Trans i18nKey="auth:signInSubheading" />
        </p>
      </div>

      <SignInMethodsContainer
        paths={paths}
        displayChildrenSection={displayChildrenSection}
        providers={providers}
        captchaSiteKey={captchaSiteKey}
        tenantSlug={tenantSlug}
      />
    </div>
  );
}

/**
 * Native sign-in page with an optional Face ID button.
 * The button is tap-to-authenticate only — no auto-fire.
 * If Face ID fails or the user cancels, the button stays visible for retry.
 */
function NativeSignInWithBiometric({
  logoUrl,
  accountName,
  primaryColor,
  secondaryColor,
  appBackgroundColor,
  appTextColor,
  signUpPath,
  passwordResetPath,
  captchaSiteKey,
  providers,
  tenantSlug,
}: {
  logoUrl?: string | null;
  accountName?: string;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor: string;
  appTextColor: string;
  signUpPath: string;
  passwordResetPath: string;
  captchaSiteKey?: string;
  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    phone: boolean;
  };
  tenantSlug?: string;
}) {
  const { hasStoredCredentials, checked } = useBiometricAuth();
  const supabase = useSupabase();
  const params = useSearchParams();

  const handleBiometricAuth = React.useCallback(
    async (credentials: { userId: string; refreshToken: string }) => {
      try {
        const { data, error } = await supabase.auth.refreshSession({
          refresh_token: credentials.refreshToken,
        });

        if (error || !data.session) {
          await clearBiometricCredentials();
          return;
        }

        await storeBiometricCredentials(
          data.session.user.id,
          data.session.refresh_token,
        );

        window.location.href = params.get('next') ?? '/home';
      } catch {
        // Failed — user can retry via button or sign in manually
      }
    },
    [supabase, params],
  );

  const showFaceId = checked && hasStoredCredentials;

  // Memoize the button element to prevent remounting on parent re-renders.
  // handleBiometricAuth is stable (useCallback with supabase + params deps),
  // so including it here does not cause unnecessary remounts.
  const faceIdButton = React.useMemo(() => {
    if (!showFaceId) return undefined;
    return (
      <NativeFaceIdButton
        primaryColor={primaryColor}
        appTextColor={appTextColor}
        onAuthenticated={handleBiometricAuth}
      />
    );
  }, [showFaceId, primaryColor, appTextColor, handleBiometricAuth]);

  return (
    <NativeSignInPage
      logoUrl={logoUrl}
      accountName={accountName}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      appBackgroundColor={appBackgroundColor}
      appTextColor={appTextColor}
      signUpPath={signUpPath}
      passwordResetPath={passwordResetPath}
      captchaSiteKey={captchaSiteKey}
      providers={providers}
      faceIdButton={faceIdButton}
      tenantSlug={tenantSlug}
    />
  );
}
